import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useSetFlag, queryKeys } from '../../api/hooks'
import { createNadDebug } from '../../utils/debugLog'
import { parseFlagError } from './flagErrors'
import type { ApiValue, FlagListItem, FlagValueType } from '../../api/client'

// NAD trace for the flag-admin write flow (feature=flags-admin). Never logs a
// sensitive flag's draft value — only key + value_type.
const flagsAdminLog = createNadDebug('dw:flags-admin:operator-ui')

// Serialize the stored value into the editor's string draft.
function valueToEditString(item: FlagListItem): string {
  if (item.value_type === 'bool') return item.value === true ? 'true' : 'false'
  if (item.value_type === 'number') {
    return item.value === null || item.value === undefined ? '' : String(item.value)
  }
  if (item.value_type === 'string') {
    return typeof item.value === 'string' ? item.value : ''
  }
  // object — pretty-print JSON so the operator can edit it.
  return JSON.stringify(item.value ?? {}, null, 2)
}

type ParseResult = { ok: true; value: ApiValue } | { ok: false; errorKey: string }

// Parse the editor draft back into a value. JSON-compat / value-type validation
// is ultimately the backend's job (FLAG_VALUE_TYPE_MISMATCH / FLAG_VALUE_NOT_JSON
// / FLAG_BODY_NOT_OBJECT); we only guard the two cases the browser can catch
// cheaply first: non-finite numbers and malformed JSON.
function parseDraft(valueType: FlagValueType, draft: string): ParseResult {
  if (valueType === 'bool') return { ok: true, value: draft === 'true' }
  if (valueType === 'number') {
    const n = Number(draft)
    if (draft.trim() === '' || !Number.isFinite(n)) {
      return { ok: false, errorKey: 'adminFlags.invalidNumber' }
    }
    return { ok: true, value: n }
  }
  if (valueType === 'string') return { ok: true, value: draft }
  // object
  try {
    return { ok: true, value: JSON.parse(draft) }
  } catch {
    return { ok: false, errorKey: 'adminFlags.invalidJson' }
  }
}

interface FlagRowProps {
  flag: FlagListItem
  // Disable saving while the list is refetching (e.g. after a conflict re-read),
  // so a Retry can't fire against a not-yet-refreshed version.
  listRefetching: boolean
}

export default function FlagRow({ flag, listRefetching }: FlagRowProps) {
  const { t } = useTranslation('pages')
  const queryClient = useQueryClient()
  const setFlag = useSetFlag()

  const [draft, setDraft] = useState(() => valueToEditString(flag))
  const [dirty, setDirty] = useState(false)
  const [rowError, setRowError] = useState<string | null>(null)
  const [conflict, setConflict] = useState(false)
  const [awaitingConfirm, setAwaitingConfirm] = useState(false)
  const [targetableBlocked, setTargetableBlocked] = useState(false)
  const lastSeenVersion = useRef(flag.version)

  // Pull in server-side changes only while this row has no local draft. A 409
  // refetch deliberately keeps the operator's dirty value for retry, while an
  // ordinary fleet refresh updates a clean editor instead of leaving stale UI.
  useEffect(() => {
    if (flag.version === lastSeenVersion.current) return
    lastSeenVersion.current = flag.version
    if (!dirty) setDraft(valueToEditString(flag))
  }, [dirty, flag])

  const busy = setFlag.isPending || listRefetching
  const editorDisabled = targetableBlocked || busy

  const onEdit = (next: string) => {
    setDraft(next)
    setDirty(true)
    setRowError(null)
    setConflict(false)
  }

  const doSave = async (confirm: boolean) => {
    const parsed = parseDraft(flag.value_type, draft)
    if (!parsed.ok) {
      setRowError(t(parsed.errorKey))
      return
    }
    setRowError(null)
    setConflict(false)
    // Optimistic concurrency: send the version we last read (0 when never set).
    // `flag` is the freshest prop (a conflict re-read updates it), so a retry
    // automatically uses the new version.
    const expectedVersion = flag.is_set && flag.version !== null ? flag.version : 0
    flagsAdminLog({
      event: 'flags-admin:save_submit',
      feature: 'flags-admin',
      concern: 'ui',
      key: flag.key,
      value_type: flag.value_type,
      sensitive: flag.sensitive,
      expected_version: expectedVersion,
    })
    try {
      await setFlag.mutateAsync({
        key: flag.key,
        value: parsed.value,
        expectedVersion,
        confirm,
      })
      // Success → useSetFlag invalidates the list; sync the editor to the saved
      // value and clear dirty (explicit, user-action-driven — never a prop sync).
      setDirty(false)
      setAwaitingConfirm(false)
    } catch (err) {
      const parsedErr =
        err instanceof Error ? parseFlagError(err) : { message: t('adminFlags.saveFailed') }
      handleError(parsedErr.code, parsedErr.message, parsedErr.actual)
    }
  }

  const handleError = (code: string | undefined, message: string, actual?: number) => {
    if (code === 'FLAG_CONFIRM_REQUIRED') {
      // Server-side sensitivity we didn't anticipate — surface the confirm step.
      setAwaitingConfirm(true)
      flagsAdminLog({
        event: 'flags-admin:save_confirm_required',
        feature: 'flags-admin',
        concern: 'ui',
        key: flag.key,
      })
      return
    }
    if (code === 'FLAG_VERSION_CONFLICT') {
      // Re-read the list; keep the operator's draft so they can retry against the
      // fresh version. The Retry stays disabled until the refetch settles (busy).
      setConflict(true)
      setAwaitingConfirm(false)
      void queryClient.invalidateQueries({ queryKey: queryKeys.flags })
      flagsAdminLog({
        event: 'flags-admin:save_conflict',
        feature: 'flags-admin',
        concern: 'ui',
        key: flag.key,
        actual,
      })
      return
    }
    if (code === 'FLAG_LOCK_TIMEOUT') {
      setRowError(t('adminFlags.lockTimeout'))
      flagsAdminLog({
        event: 'flags-admin:save_lock_timeout',
        feature: 'flags-admin',
        concern: 'ui',
        key: flag.key,
      })
      return
    }
    if (code === 'FLAG_TARGETABLE_UNSUPPORTED') {
      setTargetableBlocked(true)
      setRowError(t('adminFlags.targetableUnsupported'))
      return
    }
    // 400 value-type / not-json / etc. — surface the backend reason verbatim.
    setRowError(message)
  }

  const onSaveClick = () => {
    // Validate the draft BEFORE anything else so a sensitive flag's confirm step
    // only ever gates a valid, ready-to-send value (no confirm-then-reject).
    const parsed = parseDraft(flag.value_type, draft)
    if (!parsed.ok) {
      setRowError(t(parsed.errorKey))
      return
    }
    // Sensitive flags require an explicit confirm step before the write.
    if (flag.sensitive) {
      setAwaitingConfirm(true)
      return
    }
    void doSave(false)
  }

  const renderEditor = () => {
    if (flag.value_type === 'bool') {
      return (
        <input
          type="checkbox"
          checked={draft === 'true'}
          disabled={editorDisabled}
          onChange={(e) => onEdit(e.target.checked ? 'true' : 'false')}
          data-testid={`flag-editor-${flag.key}`}
          className="h-4 w-4 cursor-pointer accent-primary disabled:cursor-not-allowed"
        />
      )
    }
    if (flag.value_type === 'object') {
      return (
        <textarea
          value={draft}
          disabled={editorDisabled}
          onChange={(e) => onEdit(e.target.value)}
          data-testid={`flag-editor-${flag.key}`}
          rows={3}
          className="w-full rounded-lg border border-input-border bg-input-bg px-3 py-2 font-mono text-xs font-medium text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50"
        />
      )
    }
    return (
      <input
        type={flag.value_type === 'number' ? 'number' : 'text'}
        value={draft}
        disabled={editorDisabled}
        onChange={(e) => onEdit(e.target.value)}
        data-testid={`flag-editor-${flag.key}`}
        className="h-9 w-full rounded-lg border border-input-border bg-input-bg px-3 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50"
      />
    )
  }

  return (
    <tr
      className="border-t border-border-subtle align-top first:border-t-0"
      data-testid={`flag-row-${flag.key}`}
    >
      <td className="px-4 py-3">
        <div className="font-mono text-sm text-foreground">{flag.key}</div>
        <div className="text-xs font-medium text-muted-foreground">
          {flag.kind} · {flag.value_type}
          {flag.sensitive && (
            <span
              className="ml-2 rounded bg-warning/10 px-1.5 py-0.5 text-warning"
              data-testid={`flag-sensitive-${flag.key}`}
            >
              {t('adminFlags.badgeSensitive')}
            </span>
          )}
          {!flag.is_set && (
            <span className="ml-2 rounded bg-surface-2 px-1.5 py-0.5 text-muted-foreground">
              {t('adminFlags.badgeUnset')}
            </span>
          )}
        </div>
      </td>
      <td className="w-1/2 px-4 py-3">
        {renderEditor()}
        {conflict && (
          <p className="mt-1 text-xs font-medium text-warning" data-testid={`flag-conflict-${flag.key}`}>
            {t('adminFlags.conflict')}
          </p>
        )}
        {rowError && (
          <p className="mt-1 text-xs font-medium text-destructive" data-testid={`flag-error-${flag.key}`}>
            {rowError}
          </p>
        )}
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-xs font-medium text-muted-foreground">
        <div>v{flag.version ?? 0}</div>
        {flag.updated_by && <div className="truncate max-w-[10rem]">{flag.updated_by}</div>}
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-right">
        {awaitingConfirm ? (
          <div className="flex flex-col items-end gap-1">
            <span className="text-xs font-medium text-warning">{t('adminFlags.confirmSensitive')}</span>
            <div className="flex gap-2">
              <button
                onClick={() => setAwaitingConfirm(false)}
                className="px-2 py-1 text-xs font-medium text-foreground/70 hover:text-foreground cursor-pointer"
                data-testid={`flag-cancel-${flag.key}`}
              >
                {t('adminFlags.confirmCancel')}
              </button>
              <button
                onClick={() => void doSave(true)}
                disabled={busy}
                className="h-7 cursor-pointer rounded-lg bg-warning px-3 text-xs font-medium text-background hover:bg-warning/90 disabled:opacity-50"
                data-testid={`flag-confirm-${flag.key}`}
              >
                {t('adminFlags.confirmYes')}
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={onSaveClick}
            disabled={!dirty || editorDisabled}
            className="h-7 cursor-pointer rounded-lg bg-primary px-3 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid={`flag-save-${flag.key}`}
          >
            {setFlag.isPending
              ? t('adminFlags.saving')
              : conflict
                ? t('adminFlags.retry')
                : t('adminFlags.save')}
          </button>
        )}
      </td>
    </tr>
  )
}
